import type { FC, KeyboardEvent, MouseEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useUniqueId } from '../hooks/useUniqueId';
import { isKeyCode, Keys } from '../lib/keyboard';
import { mergeRefs } from '../lib/mergeRefs';
import { TumblerButton as Button } from './Button/Tumbler-Button';
import { TumblerControl as Control } from './Control/Tumbler-Control';
import { TumblerLabel as Label } from './Label/Tumbler-Label';
import { cnTumbler } from './Tumbler.cn';
import type { TumblerBaseProps } from './Tumbler.types';

import './Tumbler.css';

// @analyze-exports:disable
export const CHECKED_KEYS = [Keys.RIGHT, Keys.UP];
// @analyze-exports:disable
export const UNCHECKED_KEYS = [Keys.LEFT, Keys.DOWN];

/** Компонент, предназначенный для создания переключателя */
// @analyze-exports:disable
export const Tumbler: FC<TumblerBaseProps> = ({
    checked,
    className,
    controlRef: htmlControlRef,
    disabled,
    // eslint-disable-next-line react-hooks/rules-of-hooks
    id = useUniqueId(),
    innerRef,
    labelAfter,
    labelBefore,
    name,
    onBlur,
    onChange,
    onClick: htmlOnClick,
    onFocus,
    onKeyDown: htmlOnKeyDown,
    onKeyUp,
    tabIndex,
    title,
    autoFocus,
    required,
    size = 'm',
}) => {
    React.useEffect(() => {
        if (process.env.NODE_ENV === 'development')
            console.warn('[Tumbler]: Эта версия компонента устарела и скоро будет удалена');
    }, []);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const controlRef = useRef<HTMLInputElement>(null);

    const controlId = `control-${id}`;
    const beforeLabelId = `before-label-${id}`;
    const afterLabelId = `after-label-${id}`;

    useEffect(() => {
        if (autoFocus && buttonRef.current !== null) {
            buttonRef.current.focus();
        }
        // TODO ISL-10952: разобраться почему не все необходимые переменные указаны в deps и убрать игнор
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const labelledById = useMemo(() => {
        if (labelBefore && labelAfter) return checked ? beforeLabelId : afterLabelId;
        if (labelBefore) return beforeLabelId;
        if (labelAfter) return afterLabelId;
        return undefined;
    }, [checked, labelBefore, labelAfter, beforeLabelId, afterLabelId]);

    const onButtonClick = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            if (controlRef.current !== null) {
                controlRef.current.click();
            }
            if (buttonRef.current !== null) {
                buttonRef.current.focus();
            }
            if (htmlOnClick !== undefined) {
                htmlOnClick(event);
            }
        },
        [htmlOnClick],
    );

    const onLabelClick = useCallback(() => {
        if (buttonRef.current !== null) {
            buttonRef.current.focus();
        }
    }, []);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLButtonElement>) => {
            if (isKeyCode(event, [Keys.ENTER, ...UNCHECKED_KEYS, ...CHECKED_KEYS])) {
                event.preventDefault();
            }
            const shouldForceChange =
                (checked && isKeyCode(event, UNCHECKED_KEYS)) ||
                (!checked && isKeyCode(event, CHECKED_KEYS));
            if (shouldForceChange && controlRef.current !== null) {
                controlRef.current.click();
            }
            if (htmlOnKeyDown !== undefined) {
                htmlOnKeyDown(event);
            }
        },
        [checked, htmlOnKeyDown],
    );

    return (
        <span
            ref={innerRef}
            className={cnTumbler({ size }, [className])}
            id={id}
            title={title}
            aria-disabled={disabled}
        >
            {labelBefore && (
                <Label
                    disabled={Boolean(!checked && labelBefore && labelAfter)}
                    htmlFor={controlId}
                    id={beforeLabelId}
                    onClick={onLabelClick}
                >
                    {labelBefore}
                </Label>
            )}
            <Button
                tabIndex={disabled ? -1 : tabIndex}
                innerRef={buttonRef}
                checked={checked}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onBlur={onBlur}
                onFocus={onFocus}
                onClick={onButtonClick}
                labelledBy={labelledById}
                required={required}
            />
            <Control
                id={controlId}
                name={name}
                checked={checked}
                innerRef={mergeRefs(controlRef, htmlControlRef)}
                onChange={onChange}
                required={required}
            />
            {labelAfter && (
                <Label
                    disabled={Boolean(checked && labelBefore && labelAfter)}
                    htmlFor={controlId}
                    id={afterLabelId}
                    onClick={onLabelClick}
                >
                    {labelAfter}
                </Label>
            )}
        </span>
    );
};

Tumbler.displayName = 'Tumbler';
